/**
 * @file LanguageGenerateDropdownContainer — "Generate · {language}" selector.
 * @description SE-5570: the AI-summary language selector shared by
 * PlayerSummaryCard and AIScoutingSummaryCard, migrated off the retired
 * gluestack Menu atom onto the shared `SelectDropdown` molecule
 * (FixedPositionDropdownShell). Owns its own open state; the shell hosts the
 * panel in a native Modal (web portals to the document body), so the old
 * `useRNModal` platform fork and the SE-3823 setTimeout defer are gone. When
 * disabled (generating, or no scouting intelligence) the trigger dims and the
 * toggle is inert; the exported Container remounts the stateful component
 * when `disabled` flips so an open panel can never outlive the enabled state.
 *
 * ## Why this is still a second picker, deliberately (SE-6512)
 *
 * SE-6508 unified three language pickers into `LanguageSelector` and SE-6511
 * asked whether this one should follow. The answer taken here is NO, and the
 * reason is that the two controls have different interaction contracts, not
 * merely different styling:
 *
 * - `LanguageSelector` is CHOOSE-then-close — the selection is a preference,
 *   and it renders as a bottom sheet or a centered dialog (both overlay-portal
 *   hosts) so it can nest inside the already-open scout-report modal.
 * - This control is GENERATE-on-select — the selection spends tokens and
 *   replaces the summary on screen — and it renders as an anchored panel
 *   beside its trigger, which is what makes it legible as an action.
 *
 * Collapsing them would mean giving `LanguageSelector` a generate-on-select
 * mode AND a third presentation, i.e. two interaction contracts inside the one
 * molecule whose whole point was to have a single one. What genuinely must not
 * diverge is the VOCABULARY, and that is shared by import rather than by
 * copy-paste: `SAVE_DEFAULT_LANGUAGE_KEYS` and `isSaveDefaultPressable` come
 * from `LanguageSelector`, so both surfaces resolve the same catalog entries in
 * all six locales. Two components, one vocabulary.
 * @module features/player-detail/components/molecules/LanguageGenerateDropdown
 */
import { ChevronDown } from "lucide-react-native";
import React, { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import {
  SAVE_DEFAULT_LANGUAGE_KEYS,
  SAVE_DEFAULT_LANGUAGE_WARNING_KEY,
  isSaveDefaultPressable,
  type LanguageSelectorSaveDefault,
} from "@/components/molecules/LanguageSelector";
import SelectDropdown, {
  type SelectDropdownOption,
} from "@/components/molecules/SelectDropdown";

/** A supported summary language. */
export interface SummaryLanguage {
  /** Language code (e.g. "en"). */
  readonly value: string;
  /** Display label (e.g. "English"). */
  readonly label: string;
}

/** The id of the trailing save-as-default row. Not a locale, so not a code. */
export const SAVE_AS_DEFAULT_OPTION_ID = "save-as-default";

/** Props for {@link LanguageGenerateDropdownContainer}. */
export interface LanguageGenerateDropdownProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Supported languages */
  readonly supportedLanguages: readonly SummaryLanguage[];
  /**
   * The locale the user has actually settled on, or `undefined` when nothing
   * has resolved yet (SE-6511).
   *
   * Optional because "no row is ticked" is a real state: before the profile
   * loads nobody has chosen anything, and marking English there would be a
   * claim about a value that has not arrived. The TRIGGER still names a
   * language — see `selectedLanguageLabel`, which is what generation will
   * actually produce — because those are different questions.
   */
  readonly selectedLanguage?: string;
  /** Display label for the selected language (trigger text) */
  readonly selectedLanguageLabel: string;
  /** Whether the trigger is disabled (generating or no data) */
  readonly disabled: boolean;
  /** Invoked with the chosen language code (generates in that language) */
  readonly onSelectLanguage: (language: string) => void;
  /**
   * Optional trailing row promoting the current selection to the account
   * default (SE-6511) — the same affordance, the same copy and the same five
   * states as the `LanguageSelector` sheet, so the AI summary behaves
   * identically whichever parent it is reached through.
   */
  readonly saveAsDefault?: LanguageSelectorSaveDefault;
  /**
   * testID for the trigger pressable (SE-8547). The trigger carries an
   * accessibility label, and iOS flattens a labelled pressable's subtree, so
   * the visible "Generate · {language}" text is absent from the iOS
   * accessibility hierarchy — a native test can only reach the trigger by id.
   */
  readonly triggerTestID?: string;
}

/**
 * Stateful inner dropdown. Remounted (via `key={disabled}` in the exported
 * Container) whenever `disabled` flips, so an open panel closes the moment
 * generation starts or the data disappears — a selection can never fire while
 * the trigger is disabled. Key-reset is the repo's sanctioned alternative to a
 * setState-in-effect reset (SE-5524 / ConfirmDialog precedent).
 * @param props - Component props
 * @param props.isDark - Whether dark mode is active
 * @param props.supportedLanguages - Supported languages
 * @param props.selectedLanguage - The settled locale, or undefined when unresolved
 * @param props.selectedLanguageLabel - Display label for the selected language
 * @param props.disabled - Whether the trigger is disabled
 * @param props.onSelectLanguage - Invoked with the chosen language code
 * @param props.saveAsDefault - Optional save-as-default row and its state
 * @param props.triggerTestID - Optional testID for the trigger pressable
 * @returns The language SelectDropdown element
 */
const LanguageGenerateDropdownStateful = ({
  isDark,
  supportedLanguages,
  selectedLanguage,
  selectedLanguageLabel,
  disabled,
  onSelectLanguage,
  saveAsDefault,
  triggerTestID,
}: LanguageGenerateDropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => {
    if (disabled) return;
    setIsOpen(prev => !prev);
  }, [disabled]);
  const onClose = useCallback(() => setIsOpen(false), []);

  /*
   * The panel is held open across a selection (`keepOpenOnSelect`) and closed
   * by the locale rows themselves, because the two row KINDS want opposite
   * things: picking a locale dismisses the menu, while saving that locale as
   * the default must leave the confirmation — or the failure — on screen. The
   * shell's close-on-select is all-or-nothing, so the locale rows opt in by
   * hand rather than the save row being unable to opt out.
   */
  const localeOptions: SelectDropdownOption[] = supportedLanguages.map(
    lang => ({
      id: lang.value,
      label: lang.label,
      selected: selectedLanguage === lang.value,
      onPress: () => {
        setIsOpen(false);
        onSelectLanguage(lang.value);
      },
    })
  );

  const saveRow: SelectDropdownOption | null = saveAsDefault
    ? {
        id: SAVE_AS_DEFAULT_OPTION_ID,
        label: t(SAVE_DEFAULT_LANGUAGE_KEYS[saveAsDefault.status]),
        /*
         * The same consequence copy the sheet shows, from the same key, shown
         * on the same condition (SE-6506). The AI summary is reachable through
         * both pickers, so a warning in only one of them would be the "two
         * features wearing one name" this module's header already warns about.
         */
        description: isSaveDefaultPressable(saveAsDefault.status)
          ? t(SAVE_DEFAULT_LANGUAGE_WARNING_KEY)
          : undefined,
        // Never `selected`: this row is an action, not one of the languages,
        // and a check beside it would read as a seventh locale being chosen.
        selected: false,
        disabled: !isSaveDefaultPressable(saveAsDefault.status),
        // Sits below a divider, outside the scrolling locale list — the pinned
        // trailing group the shell provides for exactly this two-kinds case.
        startsGroup: true,
        onPress: saveAsDefault.onPress,
      }
    : null;

  const options = saveRow ? [...localeOptions, saveRow] : localeOptions;

  return (
    <SelectDropdown
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      keepOpenOnSelect
      panelClassName="max-w-56"
      triggerAccessibilityLabel={t("language.generateAccessibility")}
      triggerTestID={triggerTestID}
      triggerContent={
        <Stack
          direction="horizontal"
          className={`items-center gap-2 rounded-sm border border-outline-default bg-surface-raised px-3 py-2 ${
            isDark ? "" : "shadow-sm"
          } ${disabled ? "opacity-50" : ""}`}
        >
          <Text variant="body-strong" className="text-content-primary">
            {t("language.generate", { language: selectedLanguageLabel })}
          </Text>
          <Icon as={ChevronDown} size="xs" className="text-content-primary" />
        </Stack>
      }
      options={options}
    />
  );
};

/**
 * Key-reset wrapper: remounts the stateful inner dropdown when `disabled`
 * flips in either direction, resetting `isOpen` to false without an effect.
 * @param props - Component props (see {@link LanguageGenerateDropdownProps})
 * @returns The inner dropdown keyed on the disabled state
 */
const LanguageGenerateDropdownContainer = (
  props: LanguageGenerateDropdownProps
) => (
  <LanguageGenerateDropdownStateful
    key={props.disabled ? "disabled" : "enabled"}
    {...props}
  />
);

LanguageGenerateDropdownContainer.displayName =
  "LanguageGenerateDropdownContainer";

export default memo(LanguageGenerateDropdownContainer);
